import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import GrowthShell from '../../components/growth/GrowthShell';
import { ProposalCard } from './GrowthApprovals';
import { fmtAt } from '../../lib/growthRange';
import {
  RULE_AR, ackAlert, fetchAlerts, fetchConversation, fetchDirectorState, fetchReports, fetchSnapshots, runErrorText, runMorningNow,
  sendChat, setChatModel, setMorningEnabled, setWhatsappEnabled, snapshotLines, splitAlerts,
} from '../../lib/growthDirector';

// Growth OS «مدير النمو الذكي». Phase 1 (owner, 2026-11-02): rule-based alerts
// from growth_alerts (scanned every 10 minutes, emailed to hello@ when they
// open) and the latest daily snapshot next to the day before. Phase 2b
// (2026-11-03): the morning report on top — see MorningSection.

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }));

function AlertRow({ a, onAck }) {
  const [busy, setBusy] = useState(false);
  const ack = async () => {
    setBusy(true);
    try { await ackAlert(a.id); onAck(); } catch (e) { window.alert(`تعذّر الحفظ: ${e?.message || e}`); setBusy(false); }
  };
  return (
    <div className={`gos-alert ${a.severity}`} data-alert={a.rule} data-alert-open={a.resolved_at ? '0' : '1'}>
      <div className="gos-alert-head">
        <span className={`gos-pill ${a.severity === 'critical' ? 'bad' : 'neutral'}`}>{a.severity === 'critical' ? 'حرج' : 'تنبيه'}</span>
        <span className="gos-alert-rule">{RULE_AR[a.rule] || a.rule}</span>
        <b className="gos-alert-title">{a.title_ar}</b>
      </div>
      <div className="gos-alert-rows">
        {(Array.isArray(a.rows) ? a.rows : []).map(([k, v], i) => (
          <span key={i}><span className="gos-faint">{k}:</span> <span dir="auto">{v}</span></span>
        ))}
      </div>
      <div className="gos-alert-foot">
        <span className="gos-faint">ظهر {fmtAt(a.first_seen_at)}{a.resolved_at ? ` · انحل ${fmtAt(a.resolved_at)}` : ''}</span>
        {a.email_state === 'failed' && <span className="gos-pill bad">البريد لم يُرسل</span>}
        {a.link && <Link className="gos-alert-link" to={a.link}>فتح ←</Link>}
        {!a.resolved_at && (a.acknowledged_at
          ? <span className="gos-faint">اطّلعت عليه</span>
          : <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} disabled={busy} onClick={ack}>اطّلعت</button>)}
      </div>
    </div>
  );
}

// Phase 2b (owner, 2026-11-03): the morning report, written by director-run at
// 09:00 Riyadh while «التقرير اليومي» is on, or now from the button (the
// two comparison days). Growth OS only — WhatsApp is Phase 3a.
// Format of 2026-11-03 (owner): ≤3 points, each the fact then «رأيي:» with one
// action, then «أهم شي اليوم»; the full analysis folded under «التفاصيل».
// The first report of that day used one `text` per point — still shown.
function Report({ r }) {
  const c = r.content || {};
  const points = c.points || [];
  return (
    <div className="gos-report" data-report-day={r.day}>
      <p className="gos-report-headline">{c.headline}</p>
      {points.length > 0 && (
        <ul className="gos-report-points">{points.map((p) => (
          <li key={p.fact_key}>
            {p.fact ?? p.text}{p.changed && <span className="gos-faint"> (تغيّر)</span>}
            {p.opinion && <><br /><b>رأيي:</b> {p.opinion}</>}
          </li>
        ))}</ul>
      )}
      {c.top_action && <p className="gos-report-top" data-top-action="1"><b>أهم شي اليوم:</b> {c.top_action}</p>}
      {!points.length && !c.top_action && <p className="gos-faint">لا جديد عن آخر 7 أيام.</p>}
      {c.details && (
        <details className="gos-tech" style={{ margin: '6px 0 8px' }}>
          <summary>التفاصيل</summary>
          <p style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.8 }}>{c.details}</p>
        </details>
      )}
      <p className="gos-faint" style={{ margin: 0 }}>
        كُتب {fmtAt(r.created_at)} · {r.content?.trigger === 'manual' ? 'من الزر' : 'تلقائياً'} · نسخة التعليمات {r.content?.prompt_version ?? '—'}
        {(r.content?.dropped || []).length > 0 && ` · حُذف ${r.content.dropped.length} مكرر`}
      </p>
    </div>
  );
}

function MorningSection() {
  const [reports, setReports] = useState(undefined);
  const [state, setState] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => {
    fetchReports(14).then(setReports).catch((e) => { console.warn('[GrowthDirector] reports:', e?.message || e); setReports(null); });
    fetchDirectorState().then(setState).catch((e) => { console.warn('[GrowthDirector] state:', e?.message || e); setState(null); });
  };
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true); setMsg('يكتب التقرير… (حتى دقيقة)');
    const r = await runMorningNow();
    setBusy(false);
    setMsg(r?.ok ? `كُتب التقرير (${r.points} نقاط، $${Number(r.usd || 0).toFixed(3)})` : runErrorText(r?.error));
    load();
  };
  const toggle = async () => {
    try { await setMorningEnabled(!state.settings.morning_enabled); load(); } catch (e) { setMsg(`تعذّر الحفظ: ${e?.message || e}`); }
  };
  const toggleWa = async () => {
    try { await setWhatsappEnabled(!state.settings.whatsapp_enabled); load(); } catch (e) { setMsg(`تعذّر الحفظ: ${e?.message || e}`); }
  };
  const s = state?.settings;
  const pct = s ? Math.min(100, (state.spend / Number(s.monthly_cap_usd)) * 100) : 0;
  const [latest, ...older] = reports || [];
  return (
    <section className="gos-card" data-section="morning" style={{ marginBottom: 16 }}>
      <div className="gos-card-head">
        <h3>تقرير الصباح</h3>
        <button type="button" className="gos-btn" style={{ flex: '0 0 auto' }} disabled={busy || !state} onClick={run}>شغّل الآن</button>
      </div>
      {msg && <p className="gos-card-sub" data-run-msg="1" style={{ margin: '0 0 10px' }}>{msg}</p>}
      {state && (
        <div className="gos-budget" data-budget={pct.toFixed(1)}>
          <span>ميزانية الذكاء الاصطناعي هذا الشهر: <b dir="ltr">${state.spend.toFixed(2)}</b> من <b dir="ltr">${Number(s.monthly_cap_usd)}</b></span>
          <div className="gos-be-bar"><span style={{ width: `${pct}%` }} /></div>
          <label className="gos-scheck">
            <input type="checkbox" checked={!!s.morning_enabled} onChange={toggle} /> التقرير اليومي الساعة 09:00
          </label>
          <label className="gos-scheck" data-wa-switch="1">
            <input type="checkbox" checked={!!s.whatsapp_enabled} onChange={toggleWa} /> واتساب: التقرير والتنبيهات الحرجة على رقمك (البريد يبقى احتياطاً)
          </label>
          {state.lastRun && <span className="gos-faint">آخر تشغيل: {fmtAt(state.lastRun.at)} · {state.lastRun.action === 'run:ok' ? 'نجح' : state.lastRun.action === 'run:skipped' ? 'تُخطّي (متوقف)' : `فشل${state.lastRun.detail?.reason ? ` (${state.lastRun.detail.reason})` : ''}`}</span>}
        </div>
      )}
      {state === null && <div className="gos-empty-state"><strong>تعذّر تحميل حالة المدير</strong>أعد تحميل الصفحة.</div>}
      {reports === null && <div className="gos-empty-state"><strong>تعذّر تحميل التقارير</strong>أعد تحميل الصفحة.</div>}
      {reports && !reports.length && <div className="gos-empty-state"><strong>لا يوجد تقرير بعد</strong>اضغط «شغّل الآن» لأول تقرير.</div>}
      {latest && <Report r={latest} />}
      {older.length > 0 && (
        <details className="gos-tech" style={{ marginTop: 12 }}>
          <summary>تقارير سابقة — {older.length}</summary>
          {older.map((r) => <div key={r.id} style={{ marginTop: 10 }}><b dir="ltr">{r.day}</b><Report r={r} /></div>)}
        </details>
      )}
    </section>
  );
}

// Phase 3b (2026-11-03): the same conversation as WhatsApp, from here. The
// commands work here too («فعّل أوبوس» / «رجّع سونيت» / «التفاصيل»).
function ChatSection() {
  const [msgs, setMsgs] = useState(undefined);
  const [model, setModel] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const load = () => {
    fetchConversation(40).then(setMsgs).catch((e) => { console.warn('[GrowthDirector] chat:', e?.message || e); setMsgs(null); });
    fetchDirectorState().then((st) => setModel(st.settings.chat_model || 'sonnet')).catch(() => setModel(null));
  };
  useEffect(() => { load(); }, []);
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true); setErr(null);
    setMsgs((m) => [...(m || []), { id: `local-${Date.now()}`, channel: 'web', direction: 'in', kind: 'text', body: t }]);
    setText('');
    const r = await sendChat(t);
    setBusy(false);
    if (!r?.ok) setErr(r?.error === 'forbidden' ? 'للمالك فقط.' : 'تعذّر الرد — سُجّل الخطأ.');
    load();
  };
  const switchModel = async () => {
    try { await setChatModel(model === 'opus' ? 'sonnet' : 'opus'); load(); } catch (e) { setErr(`تعذّر الحفظ: ${e?.message || e}`); }
  };
  return (
    <section className="gos-card" data-section="chat" style={{ marginBottom: 16 }}>
      <div className="gos-card-head">
        <h3>المحادثة</h3>
        {model && <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} onClick={switchModel} data-chat-model={model}>
          {model === 'opus' ? 'أوبوس مفعّل — رجّع سونيت' : 'سونيت — فعّل أوبوس'}</button>}
      </div>
      <p className="gos-card-sub">نفس المحادثة اللي في واتساب. الأوامر تشتغل هنا بعد: «فعّل أوبوس»، «رجّع سونيت»، «التفاصيل»، «وافق P-…»، «ارفض P-… السبب»، «احفظه P-…».</p>
      {msgs === null && <div className="gos-empty-state"><strong>تعذّر تحميل المحادثة</strong>أعد تحميل الصفحة.</div>}
      <div className="gos-chat" data-chat-thread="1">
        {(msgs || []).map((m, i, all) => {
          // a proposal (Phase B): its card with the buttons, once — the last message that points at it
          const pid = m.ref?.proposal_id;
          if (pid) {
            if (all.slice(i + 1).some((x) => x.ref?.proposal_id === pid)) return null;
            return <div key={m.id} className="gos-chat-msg him" data-proposal-msg={m.ref.proposal_ref}><ProposalCard id={pid} compact /></div>;
          }
          return (
            <div key={m.id} className={`gos-chat-msg ${m.direction === 'in' ? 'me' : 'him'}`} data-direction={m.direction}>
              <span className="gos-faint">{m.direction === 'in' ? 'أنت' : 'المدير'} · {m.channel === 'whatsapp' ? 'واتساب' : 'هنا'}</span>
              <p dir="auto">{m.kind === 'voice' ? '(رسالة صوتية)' : m.body}</p>
            </div>
          );
        })}
        {busy && <div className="gos-chat-msg him"><p className="gos-faint">يكتب…</p></div>}
      </div>
      {err && <p className="gos-card-sub" data-chat-err="1">{err}</p>}
      <div className="gos-chat-input">
        <textarea className="gos-textarea" rows={2} dir="auto" value={text} placeholder="اكتب للمدير…" aria-label="رسالة للمدير"
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <button type="button" className="gos-btn" style={{ flex: '0 0 auto' }} disabled={busy || !text.trim()} onClick={send}>إرسال</button>
      </div>
    </section>
  );
}

export default function GrowthDirector({ user, profile, lang }) {
  const [alerts, setAlerts] = useState(undefined);
  const [snaps, setSnaps] = useState(undefined);
  const load = () => fetchAlerts().then(setAlerts).catch((e) => { console.warn('[GrowthDirector] alerts:', e?.message || e); setAlerts(null); });
  useEffect(() => {
    load();
    fetchSnapshots(2).then(setSnaps).catch((e) => { console.warn('[GrowthDirector] snapshots:', e?.message || e); setSnaps(null); });
  }, []);
  const split = useMemo(() => splitAlerts(alerts || []), [alerts]);
  const lines = useMemo(() => (snaps && snaps[0] ? snapshotLines(snaps[0].metrics, snaps[1]?.metrics) : null), [snaps]);

  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="ai-manager" title="مدير النمو الذكي">
      <div className="gos-hello">
        <h2>مدير النمو الذكي</h2>
        <p>تقرير الصباح يكتبه المدير من لقطات معبر عبر دور قراءة فقط. معبر يُفحص كل 10 دقائق بقواعد ثابتة (بلا ذكاء اصطناعي)، وكل تنبيه جديد يصل بريد hello@، ولقطة يومية لكل نشاط تُكتب كل ليلة.</p>
      </div>

      <MorningSection />
      <ChatSection />

      <section className="gos-card" data-section="alerts-open" style={{ marginBottom: 16 }}>
        <div className="gos-card-head"><h3>تنبيهات مفتوحة</h3><span className="gos-pcount">{alerts ? split.open.length : '…'}</span></div>
        {alerts === null && <div className="gos-empty-state"><strong>تعذّر تحميل التنبيهات</strong>أعد تحميل الصفحة.</div>}
        {alerts && !split.open.length && <div className="gos-empty-state"><strong>لا توجد تنبيهات مفتوحة</strong>كل القواعد سليمة في آخر فحص.</div>}
        {split.open.map((a) => <AlertRow key={a.id} a={a} onAck={load} />)}
      </section>

      <section className="gos-card" data-section="snapshot" style={{ marginBottom: 16 }}>
        <div className="gos-card-head">
          <h3>لقطة اليوم المكتمل الأخير</h3>
          {snaps && snaps[0] && <span className="gos-faint" dir="ltr">{snaps[0].metrics?.day || snaps[0].day}</span>}
        </div>
        {snaps === null && <div className="gos-empty-state"><strong>تعذّر تحميل اللقطة</strong>أعد تحميل الصفحة.</div>}
        {snaps && !snaps.length && <div className="gos-empty-state"><strong>لا توجد لقطات بعد</strong>تُكتب أول لقطة الساعة 00:40 بتوقيت الرياض.</div>}
        {lines && (
          <table className="gos-table" data-snapshot-day={snaps[0].day}>
            <thead><tr><th>المؤشر</th><th>يوم اللقطة</th><th>اليوم الذي قبله</th></tr></thead>
            <tbody>{lines.map((l) => (
              <tr key={l.label}><td>{l.label}</td><td className="num">{fmt(l.value)}</td><td className="num gos-faint">{l.prev == null ? '—' : fmt(l.prev)}</td></tr>
            ))}</tbody>
          </table>
        )}
        <p className="gos-card-sub" style={{ margin: '10px 0 0' }}>أعداد ومجاميع فقط: لا أسماء ولا أرقام جوال ولا عناوين. حسابات الفريق وطلبات الاختبار المستبعدة في «الربحية» غير محسوبة.</p>
      </section>

      {split.resolved.length > 0 && (
        <details className="gos-card gos-tech" data-section="alerts-resolved">
          <summary>تنبيهات انحلّت (آخر 30 يوماً) — {split.resolved.length}</summary>
          <div style={{ marginTop: 10 }}>{split.resolved.map((a) => <AlertRow key={a.id} a={a} onAck={load} />)}</div>
        </details>
      )}
    </GrowthShell>
  );
}
